import { DEFAULT_LANGUAGE, normalizeLanguageTag, type ParsedQpStack, type SDK_ViewFile, strings } from "@quickpoint.me/shared";
import { selector } from "recoil";
import parser from "../lib/parser";
import { type NDRCC, NeuroDivergentColorChoice, UseNDRColors } from "./accessibility";
import { PageElementIsLoadedFamily, PageElementsFamily, PageIndex, RenderedPageElementsToDisplay, createLocation } from "./page";
import { RawCode, StackCfKey } from "./stack";
import { IsInAuthor } from "./ui";

// https://stackoverflow.com/questions/43118692/typescript-filter-out-nulls-from-an-array
function notEmpty<TValue>(
  value: TValue | null | undefined,
): value is TValue {
  return value !== null && value !== undefined;
}

export const AllIDsInPageElementsFamily = selector<number[]>({
  key: "VS_AllIDsInPageElementsFamily",
  get: ({ get }) => {
    const pageIndex = get(PageIndex)
    const renderedPageElementsToDisplay = get(RenderedPageElementsToDisplay(pageIndex));
    const ids = renderedPageElementsToDisplay.map((element) => element.props.atomId);
    return ids;
  },
});

// Element content is a string, a parsed React element, or an array of either
// (markdown becomes elements). Walk it back to plain text for a spoken label.
function extractText(content: unknown): string {
  if (content === null || content === undefined || typeof content === "boolean") return "";
  if (typeof content === "string" || typeof content === "number") return String(content);
  if (Array.isArray(content)) return content.map(extractText).join("");
  const el = content as { props?: { children?: unknown } };
  if (el.props && "children" in el.props) return extractText(el.props.children);
  return "";
}

// Strip leftover markdown/HTML/emphasis and collapse whitespace so the label
// reads cleanly to a screen reader; cap length so a paragraph-as-title can't
// run away.
function cleanLabel(raw: string): string {
  return raw
    .replace(/<[^>]+>/g, "")
    .replace(/[*_`~#>]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 120);
}

// Title-bearing element types, in the priority a slide's NAME should take:
// its title, else a supertitle (kicker), else a subtitle.
const TITLE_TYPES = ["title:", "supertitle:", "subtitle:"] as const;

/**
 * The current slide's own title text, for accessible naming (WCAG 2.4.2 Page
 * Titled / 2.4.6 Headings and Labels). Empty string when the slide has no
 * title-bearing element — callers fall back to the positional "Slide N of M".
 */
export const CurrentSlideTitle = selector<string>({
  key: "VS_CurrentSlideTitle",
  get: ({ get }) => {
    const pageIndex = get(PageIndex);
    const rendered = get(RenderedPageElementsToDisplay(pageIndex));
    for (const wanted of TITLE_TYPES) {
      for (const element of rendered) {
        const loc = createLocation(pageIndex, element.props.atomId);
        const { data } = get(PageElementsFamily(loc));
        if (data.type === wanted) {
          const text = cleanLabel(extractText(data.content));
          if (text) return text;
        }
      }
    }
    return "";
  },
});

// a selector that reports if all loadable page elements are loaded
export const AllLoaded = selector<boolean>({
  key: "VS_AllLoaded",
  get: ({ get }) => {
    const ids = get(AllIDsInPageElementsFamily);
    const pageIndex = get(PageIndex);
    const values: boolean[] = [];
    for (const id of ids) {
      const loc = createLocation(pageIndex, id);
      const val = get(PageElementIsLoadedFamily(loc));
      if (val !== undefined) {
        values.push(val);
      }
    }
    return values.length === 0 ? false : !values.includes(false);
  },
});


/** Plus owners publish without the "Made with QuickPoint" badge. */
export const HideAttribution = selector<boolean>({
  key: "VS_HideAttribution",
  get: ({ get }) => {
    const rawCode = get(RawCode(get(StackCfKey)));
    return !!rawCode?.hideAttribution;
  },
});

// maps the file name to the image URL
export const AudioFileMaps = selector<SDK_ViewFile[]>({
  key: "VS_AudioFileMaps",
  get: ({ get }) => {
    const stackPath = get(StackCfKey);
    const rawCode = get(RawCode(stackPath));
    if (rawCode?.audioFiles) {
      const files = rawCode.audioFiles.filter(notEmpty);
      return files;
    }
    return [];
  },
});

// maps the file name to the image URL
export const ImageMaps = selector<SDK_ViewFile[]>({
  key: "VS_ImageMaps",
  get: ({ get }) => {
    const stackPath = get(StackCfKey);
    const rawCode = get(RawCode(stackPath));
    if (rawCode?.images) {
      const images = rawCode.images.filter(notEmpty);
      return images;
    }
    return [];
  },
});

// maps the file name to the image URL
export const DataFileMaps = selector<SDK_ViewFile[]>({
  key: "VS_DataFileMaps",
  get: ({ get }) => {
    const stackPath = get(StackCfKey);
    const rawCode = get(RawCode(stackPath));
    if (rawCode?.dataFiles) {
      const files = rawCode.dataFiles.filter(notEmpty);
      return files;
    }
    return [];
  },
});


function replaceModifiersWithNDRColors(parsedCode: ParsedQpStack, colors: NDRCC) {
  for (const page of parsedCode) {
    for (const line of page) {
      if (line.type === "background:") line.content = colors.background;
      if (line.modifier) {
        if (line.modifier["color:"]) {
          line.modifier["color:"] = colors.text;
        }
        if (line.modifier["background:"]) {
          line.modifier["background:"] = colors.background;
        }
      }
    }
  }
}

export const ParsedCode = selector<ParsedQpStack>({
  key: "VS_ParsedCodeSelector",
  get: ({ get }) => {
    const stackPath = get(StackCfKey);
    const rawCodeAtom = get(RawCode(stackPath));
    const useNDRColors = get(UseNDRColors);
    const ndrColors = get(NeuroDivergentColorChoice)
    if (rawCodeAtom?.code) {
      const parsedCode = parser(rawCodeAtom.code);
      if (useNDRColors) {
        replaceModifiersWithNDRColors(parsedCode, ndrColors);
      };

      return parsedCode
    }

    return [];
  },
});


// The deck's declared language for the lang attribute (WCAG 3.1.1). Read from
// the deck-level `language:` directive (author tooling auto-fills it from the
// browser at creation; the viewer only reads + validates via the shared
// helper). Anything malformed or absent falls back to "en".
export const DeckLanguage = selector<string>({
  key: "VS_DeckLanguage",
  get: ({ get }) => {
    const stackPath = get(StackCfKey);
    const code = get(RawCode(stackPath))?.code;
    if (!code) return DEFAULT_LANGUAGE;
    // First `language:` directive wins; value is the rest of that line.
    const match = code.match(/^[ \t]*language:[ \t]*(.+)$/im);
    return normalizeLanguageTag(match?.[1]);
  },
});

export const StackName = selector<string>({
  key: "VS_StackName",
  get: ({ get }) => {
    const isInAuthor = get(IsInAuthor);
    if (isInAuthor) return strings.utility.inAuthor;
    const locations = window.location.pathname.split("/");
    const stackName = locations[2];
    return stackName;
  },
});

export const NumberOfPages = selector<number>({
  key: "VS_NumberOfPages",
  get: ({ get }) => {
    const parsedCode = get(ParsedCode);
    return parsedCode.length;
  },
});

export const OwnerName = selector<string>({
  key: "VS_OwnerName",
  get: ({ get }) => {
    const isInAuthor = get(IsInAuthor);
    if (isInAuthor) return strings.utility.inAuthor;
    const locations = window.location.pathname.split("/");
    const ownerName = locations[1];
    return ownerName;
  },
});

export const StackId = selector<string>({
  key: "VS_StackId",
  get: ({ get }) => {
    const stackPath = get(StackCfKey);
    const rawCodeAtom = get(RawCode(stackPath));
    return rawCodeAtom.stackId;
  },
});
export interface LastStoryAndPageRead { lastStackCfKey: string, lastPage: number, lastOwner: string, time: number }



